import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import jsPDF from "jspdf";

import { Navbar } from "@/components/layout/Nav";
import { Carlos, Ana, Mascot } from "@/components/characters/Characters";
import { ComicPanel, SpeechBubble, FactBox, DoDontCompare } from "@/components/magazine/Panels";
import { CHAPTERS } from "@/data/chapters";
import { useLang } from "@/context/LangContext";
import { MAGAZINE } from "@/constants/testIds";
import { Progress } from "@/components/ui/progress";
import { Illustration, useIllustrations } from "@/context/IllustrationsContext";

const Cover = ({ chapter, lang }) => (
  <div className="brutal p-8 sm:p-14 bg-canales-yellow relative overflow-hidden text-center">
    <div className="absolute inset-0 opacity-30 pointer-events-none"
      style={{ backgroundImage: "radial-gradient(#000 1.5px, transparent 1.5px)", backgroundSize: "20px 20px" }} />
    <div className="relative">
      <div className="inline-block bg-canales-red text-white px-4 py-1 border-4 border-black rounded-full font-display font-black tracking-widest uppercase text-sm">
        {lang === "es" ? "Revista Nº 1" : "Issue #1"}
      </div>
      <h1 className="mt-6 font-display font-black text-5xl sm:text-7xl leading-[0.95]">
        {chapter.title[lang]}
      </h1>
      <p className="mt-4 font-display text-xl sm:text-2xl">{chapter.subtitle[lang]}</p>
      <div className="mt-6">
        <Illustration
          keyName="cover"
          className="mx-auto max-h-96 rounded-2xl border-4 border-black shadow-comic-lg"
          fallback={
            <div className="flex justify-center gap-4 flex-wrap">
              <Carlos size={120} waving />
              <Ana size={120} />
              <Mascot size={140} />
            </div>
          }
        />
      </div>
    </div>
  </div>
);

const SceneCard = ({ scene, lang, tilt = 0 }) => {
  const isCarlos = scene.speaker === "carlos";
  const CharacterComp = isCarlos ? Carlos : Ana;
  const speakerName = isCarlos ? "CARLOS" : "ANA";
  const bg = isCarlos ? "#eaf3ff" : "#ffefef";

  return (
    <ComicPanel tilt={tilt} color={bg} className="mb-6">
      <div className={`flex gap-4 items-start ${isCarlos ? "" : "flex-row-reverse"}`}>
        <div className="shrink-0">
          <CharacterComp size={100} waving={isCarlos} />
        </div>
        <SpeechBubble from={speakerName}>{scene.text[lang]}</SpeechBubble>
      </div>
    </ComicPanel>
  );
};

export default function Magazine() {
  const { t, lang } = useLang();
  const [idx, setIdx] = useState(0);
  const current = CHAPTERS[idx];
  const progress = useMemo(() => Math.round(((idx + 1) / CHAPTERS.length) * 100), [idx]);
  const isCover = current.kind === "cover";
  const { map: illuMap } = useIllustrations() || { map: {} };

  const next = () => setIdx((i) => Math.min(CHAPTERS.length - 1, i + 1));
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    // Cover
    doc.setFillColor(255, 209, 0);
    doc.rect(0, 0, W, H, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(38);
    doc.setTextColor("#0B0B14");
    doc.text("CANALES FURNITURE", W / 2, H / 3, { align: "center" });
    doc.setFontSize(22);
    doc.text(lang === "es" ? "Revista Educativa Interactiva" : "Interactive Learning Magazine", W / 2, H / 3 + 40, { align: "center" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text(lang === "es" ? "Carlos, Ana y el Sillón Canales" : "Carlos, Ana and the Canales Sofa", W / 2, H / 3 + 74, { align: "center" });

    // Chapters
    CHAPTERS.forEach((c, i) => {
      if (c.kind === "cover") return;
      doc.addPage();

      doc.setFillColor(255, 248, 231);
      doc.rect(0, 0, W, H, "F");

      doc.setDrawColor(0);
      doc.setLineWidth(3);
      doc.rect(24, 24, W - 48, H - 48);

      doc.setTextColor("#005BBB");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.text(`${i}. ${c.title[lang]}`, 48, 74);

      let y = 110;
      doc.setTextColor("#0B0B14");
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");

      // Illustration if present
      const illu = illuMap[`chapter_${c.id}`];
      if (illu) {
        try {
          const dataUri = `data:${illu.mime_type};base64,${illu.data}`;
          doc.addImage(dataUri, "PNG", 48, y, W - 96, 180);
          y += 200;
        } catch {
          /* ignore image errors */
        }
      }

      (c.scenes || []).forEach((s) => {
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#E3000F");
        doc.text(`${s.speaker.toUpperCase()}:`, 48, y);
        y += 16;
        doc.setFont("helvetica", "normal");
        doc.setTextColor("#0B0B14");
        const lines = doc.splitTextToSize(s.text[lang], W - 96);
        doc.text(lines, 48, y);
        y += lines.length * 14 + 12;
      });

      if (c.fact) {
        y += 6;
        doc.setFillColor(255, 209, 0);
        const factLines = doc.splitTextToSize(c.fact.text[lang], W - 130);
        const boxH = 24 + factLines.length * 14 + 10;
        doc.rect(48, y, W - 96, boxH, "F");
        doc.setDrawColor(0);
        doc.setLineWidth(2);
        doc.rect(48, y, W - 96, boxH);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(lang === "es" ? c.fact.title_es : c.fact.title_en, 58, y + 16);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.text(factLines, 58, y + 32);
        y += boxH + 12;
      }

      if (c.dos) {
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#16a34a");
        doc.text(lang === "es" ? "✔ QUÉ HACER:" : "✔ WHAT TO DO:", 48, y);
        y += 14;
        doc.setFont("helvetica", "normal");
        doc.setTextColor("#0B0B14");
        c.dos.do[lang].forEach((it) => { doc.text(`• ${it}`, 60, y); y += 14; });
        y += 6;
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#dc2626");
        doc.text(lang === "es" ? "✘ QUÉ NO HACER:" : "✘ WHAT NOT TO DO:", 48, y);
        y += 14;
        doc.setFont("helvetica", "normal");
        doc.setTextColor("#0B0B14");
        c.dos.dont[lang].forEach((it) => { doc.text(`• ${it}`, 60, y); y += 14; });
      }
    });

    // Back cover
    doc.addPage();
    doc.setFillColor(0, 91, 187);
    doc.rect(0, 0, W, H, "F");
    doc.setTextColor("#fff");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.text(lang === "es" ? "¡Ahora tú eres una Estrella Canales!" : "Now YOU are a Canales Star!", W / 2, H / 2, { align: "center" });
    doc.setFontSize(14);
    doc.text("© Canales Furniture", W / 2, H / 2 + 40, { align: "center" });

    doc.save(`canales-revista-${lang}.pdf`);
  };

  return (
    <div className="min-h-screen bg-halftone-blue">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid={MAGAZINE.root}>
        {/* progress */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex-1">
            <Progress value={progress} className="h-3 border-2 border-black bg-white" />
          </div>
          <span className="font-display font-bold text-sm">
            {idx + 1} / {CHAPTERS.length}
          </span>
          <button
            onClick={exportPDF}
            data-testid="magazine-export-pdf"
            className="brutal-btn bg-canales-red text-white px-3 py-1 text-sm flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>

        {/* chapter dots */}
        <div className="flex flex-wrap gap-2 mb-6">
          {CHAPTERS.map((c, i) => (
            <button
              key={c.id}
              data-testid={MAGAZINE.chapterCard(c.id)}
              onClick={() => setIdx(i)}
              className={`px-3 py-1 rounded-full border-2 font-display font-bold text-xs ${
                i === idx
                  ? "bg-canales-blue text-white border-black"
                  : "bg-white text-black border-black/40 hover:border-black"
              }`}
            >
              {i + 1}. {c.title[lang]}
            </button>
          ))}
        </div>

        {/* content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.35 }}
          >
            {isCover ? (
              <Cover chapter={current} lang={lang} />
            ) : (
              <div>
                <div className="mb-4 flex items-center gap-3">
                  <h2 className="font-display font-black text-3xl sm:text-5xl">
                    {current.title[lang]}
                  </h2>
                </div>

                <Illustration
                  keyName={`chapter_${current.id}`}
                  className="w-full max-h-80 rounded-2xl border-4 border-black shadow-comic-lg mb-4"
                  fallback={null}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  {current.scenes?.map((s, i) => (
                    <SceneCard key={i} scene={s} lang={lang} tilt={i % 2 === 0 ? -0.5 : 0.6} />
                  ))}
                </div>

                {current.fact && (
                  <FactBox
                    variant={current.fact.variant}
                    title={lang === "es" ? current.fact.title_es : current.fact.title_en}
                  >
                    {current.fact.text[lang]}
                  </FactBox>
                )}

                {current.dos && (
                  <DoDontCompare
                    doItems={current.dos.do[lang]}
                    dontItems={current.dos.dont[lang]}
                  />
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* nav */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={prev}
            data-testid={MAGAZINE.prev}
            disabled={idx === 0}
            className="brutal-btn bg-white px-4 py-2 disabled:opacity-40 flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> {t("previous")}
          </button>
          <button
            onClick={next}
            data-testid={MAGAZINE.next}
            disabled={idx === CHAPTERS.length - 1}
            className="brutal-btn bg-canales-blue text-white px-4 py-2 disabled:opacity-40 flex items-center gap-2"
          >
            {t("next")} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
